/**
 * The built-in logo changed several times (Atomic CRM mark, the Avante "A",
 * then a compass rose, now the S1 sail). The configuration persists logo
 * URLs (localStorage store and the `configuration` row), and Vite inlines
 * those small SVGs as data URIs, so a device or database that saved the
 * configuration under an older build kept showing the old mark forever.
 *
 * Every pre-sail built-in logo was drawn on an 800x800 viewBox and served as
 * `logo_atomic_crm_{dark,light}.svg`; the sail is 64x64. Custom uploads are
 * left untouched.
 */
const LEGACY_FILE =
  /\/logo_atomic_crm_(?:dark|light)(?:-[\w-]+)?\.svg(?:[?#].*)?$/;
const LEGACY_VIEWBOX = /viewBox=["']0 0 800 800["']/;
const SVG_DATA_URI = /^data:image\/svg\+xml(;base64)?,/;

const decodeSvgDataUri = (value: string): string | null => {
  const match = SVG_DATA_URI.exec(value);
  if (!match) return null;
  const payload = value.slice(match[0].length);
  try {
    return match[1] ? atob(payload) : decodeURIComponent(payload);
  } catch {
    return null;
  }
};

export const isLegacyBuiltInLogo = (value: string | undefined): boolean => {
  if (!value) return false;
  const svg = decodeSvgDataUri(value);
  if (svg != null) return LEGACY_VIEWBOX.test(svg);
  return LEGACY_FILE.test(value);
};

/** Swaps stored pre-sail built-in logos for the current defaults. */
export const withCurrentBuiltInLogos = <
  T extends { darkModeLogo: string; lightModeLogo: string },
>(
  config: T,
  defaults: { darkModeLogo: string; lightModeLogo: string },
): T => {
  const darkIsLegacy = isLegacyBuiltInLogo(config.darkModeLogo);
  const lightIsLegacy = isLegacyBuiltInLogo(config.lightModeLogo);
  if (!darkIsLegacy && !lightIsLegacy) return config;
  return {
    ...config,
    darkModeLogo: darkIsLegacy ? defaults.darkModeLogo : config.darkModeLogo,
    lightModeLogo: lightIsLegacy
      ? defaults.lightModeLogo
      : config.lightModeLogo,
  };
};
